/**
 * tabRegistry.js - 전체 페이지/탭 라우팅 레지스트리
 *
 * AICC 애플리케이션의 모든 페이지 컴포넌트를 lazy-load로 등록하고
 * 탭 ID, 경로, 아이콘, 권한을 매핑하는 중앙 라우팅 설정
 *
 * @see AppRouter, NavigationSidebar
 */

import { lazy } from 'react';

// ============================================
// Lazy-loaded page components (code splitting)
// ============================================

// Heavy library pages
const ChatPage = lazy(() => import('../components/ChatPage'));
const WorkflowEditorPage = lazy(() => import('../pages/WorkflowEditorPage'));
const WorkflowTestPage = lazy(() => import('../pages/WorkflowTestPage'));

// General pages
const ToolManagerPage = lazy(() => import('../pages/ToolManagerPage'));
const VariableManagerPage = lazy(() => import('../pages/VariableManagerPage'));
const AnnouncementListPage = lazy(() => import('../pages/AnnouncementListPage'));
const OrchestratorChatPage = lazy(() => import('../pages/OrchestratorChatPage'));

// Admin pages
const AdminDashboardPage = lazy(() =>
  import('../pages/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage }))
);
const TenantManagerPage = lazy(() =>
  import('../pages/admin/TenantManagerPage').then((m) => ({ default: m.TenantManagerPage }))
);
const TenantDetailPage = lazy(() =>
  import('../pages/admin/TenantDetailPage').then((m) => ({ default: m.TenantDetailPage }))
);
const ConversationListPage = lazy(() =>
  import('../pages/admin/ConversationListPage').then((m) => ({ default: m.ConversationListPage }))
);
const ConversationViewerPage = lazy(() =>
  import('../pages/admin/ConversationViewerPage').then((m) => ({
    default: m.ConversationViewerPage,
  }))
);
const UserManagerPage = lazy(() =>
  import('../pages/admin/UserManagerPage').then((m) => ({ default: m.UserManagerPage }))
);
const ChannelAnalyticsPage = lazy(() => import('../pages/admin/ChannelAnalyticsPage'));
const AuditLogPage = lazy(() => import('../pages/admin/AuditLogPage'));
const LoginHistoryPage = lazy(() => import('../pages/admin/LoginHistoryPage'));
const AnnouncementManagerPage = lazy(() =>
  import('../pages/admin/AnnouncementManagerPage').then((m) => ({
    default: m.AnnouncementManagerPage,
  }))
);
const LlmModelManagerPage = lazy(() =>
  import('../pages/admin/LlmModelManagerPage').then((m) => ({ default: m.LlmModelManagerPage }))
);
const SessionMonitorPage = lazy(() =>
  import('../pages/admin/SessionMonitorPage').then((m) => ({ default: m.SessionMonitorPage }))
);
const SystemPromptLibraryPage = lazy(() =>
  import('../pages/admin/SystemPromptLibraryPage').then((m) => ({
    default: m.SystemPromptLibraryPage,
  }))
);
const PermissionManagementPage = lazy(() => import('../pages/admin/PermissionManagementPage'));
const WallboardPage = lazy(() => import('../pages/admin/WallboardPage'));
const ServiceAnalyticsPage = lazy(() => import('../pages/admin/ServiceAnalyticsPage'));
const EscalationDashboardPage = lazy(() => import('../pages/admin/EscalationDashboardPage'));

/**
 * Tab Registry — maps URL paths to page components and metadata.
 * Used by TabContext to resolve pages and check permissions.
 */
export const TAB_REGISTRY = {
  // General pages
  '/chat': { component: ChatPage, label: '모델 플레이그라운드', minRole: 'USER' },
  '/workflow-editor': {
    component: WorkflowEditorPage,
    label: '워크플로우 에디터',
    minRole: 'USER',
  },
  '/workflow-test': { component: WorkflowTestPage, label: '워크플로우 테스터', minRole: 'USER' },
  '/tool-manager': { component: ToolManagerPage, label: '도구 관리', minRole: 'USER' },
  '/variable-manager': { component: VariableManagerPage, label: '변수 관리', minRole: 'USER' },
  '/announcements': { component: AnnouncementListPage, label: '공지사항', minRole: 'USER' },
  '/orchestrator-chat': {
    component: OrchestratorChatPage,
    label: '오케스트레이터',
    minRole: 'USER',
  },

  // Admin pages — TENANT_ADMIN
  '/admin': { component: AdminDashboardPage, label: '대시보드', minRole: 'TENANT_ADMIN' },
  '/admin/sessions': {
    component: SessionMonitorPage,
    label: '실시간 세션',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/conversations': {
    component: ConversationListPage,
    label: '대화 로그',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/monitoring': {
    component: WallboardPage,
    label: '실시간 현황판',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/analytics': {
    component: ServiceAnalyticsPage,
    label: '서비스 분석',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/channels': {
    component: ChannelAnalyticsPage,
    label: '채널 분석',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/tenants': {
    component: TenantManagerPage,
    label: '테넌트 · 채널',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/announcements': {
    component: AnnouncementManagerPage,
    label: '공지사항',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/system-prompts': {
    component: SystemPromptLibraryPage,
    label: 'Prompt Studio',
    minRole: 'TENANT_ADMIN',
  },
  '/admin/chat-tts': {
    component: ChatPage,
    label: 'TTS 플레이그라운드',
    minRole: 'TENANT_ADMIN',
    props: { enableTTS: true },
  },

  // Admin pages — TENANT_ADMIN (monitoring dashboards)
  '/admin/wallboard': { component: WallboardPage, label: '실시간 현황판', minRole: 'TENANT_ADMIN' },
  '/admin/service-analytics': {
    component: ServiceAnalyticsPage,
    label: '서비스 분석',
    minRole: 'TENANT_ADMIN',
  },

  '/admin/escalations': {
    component: EscalationDashboardPage,
    label: '에스컬레이션 분석',
    minRole: 'TENANT_ADMIN',
  },

  // Admin pages — GLOBAL_ADMIN
  '/admin/audit-logs': { component: AuditLogPage, label: '감사 로그', minRole: 'GLOBAL_ADMIN' },
  '/admin/login-history': {
    component: LoginHistoryPage,
    label: '로그인 이력',
    minRole: 'GLOBAL_ADMIN',
  },
  '/admin/users': { component: UserManagerPage, label: '사용자 관리', minRole: 'GLOBAL_ADMIN' },
  '/admin/permissions': {
    component: PermissionManagementPage,
    label: '권한 관리',
    minRole: 'GLOBAL_ADMIN',
  },
  '/admin/llm-models': {
    component: LlmModelManagerPage,
    label: 'LLM 모델 관리',
    minRole: 'GLOBAL_ADMIN',
  },
};

/**
 * Dynamic routes with path parameters.
 * These are resolved when a page navigates internally (e.g., list → detail).
 */
export const DYNAMIC_ROUTES = [
  {
    pattern: '/admin/tenants/:tenantId',
    component: TenantDetailPage,
    label: '테넌트 상세',
    minRole: 'TENANT_ADMIN',
  },
  {
    pattern: '/admin/conversations/:id',
    component: ConversationViewerPage,
    label: '대화 상세',
    minRole: 'TENANT_ADMIN',
  },
];
